/**
 * packagePinsStore — per-connection pinned packages and last-viewed memory
 * for Oracle Package Explorer. Persisted to localStorage via zustand's
 * persist middleware.
 *
 * Slice 2: only `lastViewedByConnection` is wired. Pin actions are stubbed
 * until slice 3.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createGuardedJsonStorage } from '../../../../core/storage/guardedJsonStorage';

export type PinnedPackage = { schema: string; name: string; pinnedAt: string };
export type LastViewed = { schema: string; name: string };

type PackagePinsState = {
  pinsByConnection: Record<string, PinnedPackage[]>;
  lastViewedByConnection: Record<string, LastViewed>;

  // selectors
  getPins: (connectionId: string) => PinnedPackage[];
  isPinned: (connectionId: string, schema: string, name: string) => boolean;
  getLastViewed: (connectionId: string) => LastViewed | undefined;

  // last-viewed actions (slice 2)
  setLastViewed: (connectionId: string, pkg: LastViewed) => void;
  clearLastViewed: (connectionId: string) => void;

  // pin actions — implemented in slice 3, placeholders throw in slice 2
  addPin: (connectionId: string, schema: string, name: string) => void;
  removePin: (connectionId: string, schema: string, name: string) => void;
  reorderPins: (connectionId: string, fromIndex: number, toIndex: number) => void;
  pruneConnection: (connectionId: string) => void;
};

const EMPTY_PINS: readonly PinnedPackage[] = Object.freeze([]);

export const usePackagePinsStore = create<PackagePinsState>()(
  persist(
    (set, get) => ({
      pinsByConnection: {},
      lastViewedByConnection: {},

      getPins: (connectionId) => (get().pinsByConnection[connectionId] ?? EMPTY_PINS) as PinnedPackage[],
      isPinned: (connectionId, schema, name) => {
        const pins = get().pinsByConnection[connectionId] ?? EMPTY_PINS;
        return pins.some((p) => p.schema === schema && p.name === name);
      },
      getLastViewed: (connectionId) => get().lastViewedByConnection[connectionId],

      setLastViewed: (connectionId, pkg) => {
        set((state) => ({
          lastViewedByConnection: { ...state.lastViewedByConnection, [connectionId]: pkg },
        }));
      },
      clearLastViewed: (connectionId) => {
        set((state) => {
          const next = { ...state.lastViewedByConnection };
          delete next[connectionId];
          return { lastViewedByConnection: next };
        });
      },

      addPin: (connectionId, schema, name) => {
        set((state) => {
          const current = state.pinsByConnection[connectionId] ?? [];
          if (current.some((p) => p.schema === schema && p.name === name)) return state;
          const next: PinnedPackage = { schema, name, pinnedAt: new Date().toISOString() };
          return {
            pinsByConnection: { ...state.pinsByConnection, [connectionId]: [...current, next] },
          };
        });
      },
      removePin: (connectionId, schema, name) => {
        set((state) => {
          const current = state.pinsByConnection[connectionId] ?? [];
          const next = current.filter((p) => !(p.schema === schema && p.name === name));
          if (next.length === current.length) return state;
          return {
            pinsByConnection: { ...state.pinsByConnection, [connectionId]: next },
          };
        });
      },
      reorderPins: (connectionId, fromIndex, toIndex) => {
        set((state) => {
          const current = state.pinsByConnection[connectionId] ?? [];
          if (
            fromIndex < 0 ||
            toIndex < 0 ||
            fromIndex >= current.length ||
            toIndex >= current.length
          ) return state;
          const next = [...current];
          const [moved] = next.splice(fromIndex, 1);
          next.splice(toIndex, 0, moved);
          return {
            pinsByConnection: { ...state.pinsByConnection, [connectionId]: next },
          };
        });
      },
      pruneConnection: (connectionId) => {
        set((state) => {
          const nextPins = { ...state.pinsByConnection };
          delete nextPins[connectionId];
          const nextLast = { ...state.lastViewedByConnection };
          delete nextLast[connectionId];
          return { pinsByConnection: nextPins, lastViewedByConnection: nextLast };
        });
      },
    }),
    {
      name: 'jam-sql-package-pins',
      // Crash-proof write (core/storage/guardedJsonStorage.ts): a full origin must not throw out of
      // set(). No toast here — these are shortcuts to packages that still exist in the database;
      // the drop is still reported to telemetry and the console.
      storage: createGuardedJsonStorage(),
      version: 1,
      partialize: (state) => ({
        pinsByConnection: state.pinsByConnection,
        lastViewedByConnection: state.lastViewedByConnection,
      }),
    },
  ),
);
